// Sugalarm — kiosk volume popover (cycle 22).
//
// Anchored to the right edge of the volume button in the header. Closes
// on outside click and on Escape. Pure UI state — does NOT trigger
// urgent overlay or alarm flow.
//
// Sized for the 1024×600 kiosk: slider thumb has a finger-friendly
// 28×28 hit area; total height ≤ 130 px so the popover never overlaps
// the next-row content.

import { useEffect, useRef } from "react";

interface Props {
  state: { level: number; muted: boolean };
  onSetLevel: (n: number) => void;
  onSetMuted: (m: boolean) => void;
  onClose: () => void;
  /** Cycle 317: hide the Mute control (the Android tablet kiosk floors its
   *  alarm/TTS volume instead — a glucose kiosk must never be one-tap
   *  silenced). Default false → the Pi loopback agent keeps Mute. */
  hideMute?: boolean;
}

export function VolumePopover({
  state,
  onSetLevel,
  onSetMuted,
  onClose,
  hideMute = false,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    // Use mousedown so the close fires before the click would re-open the
    // popover via the toggle button.
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Kiosk volume"
      style={{
        position: "fixed",
        top: 44,
        right: 12,
        width: 280,
        padding: "12px 14px",
        background: "var(--bg-elev)",
        border: "0.5px solid var(--line-strong)",
        borderRadius: 14,
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        zIndex: 50,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <input
          type="range"
          min={0}
          max={100}
          value={state.level}
          onChange={(e) => onSetLevel(Number(e.target.value))}
          aria-label="Volume level"
          className="kiosk-volume-slider"
          style={{
            flex: 1,
            height: 28,
            // Background and thumb sizing handled by the global CSS rule
            // below so the kiosk-only overrides don't bleed into the
            // web frame's range inputs.
          }}
        />
        <span
          className="tnum"
          style={{
            fontFamily: "var(--hero-font)",
            fontWeight: 600,
            fontSize: 14,
            color: "var(--ink-2)",
            minWidth: 40,
            textAlign: "right",
          }}
        >
          {state.level}%
        </span>
      </div>

      {!hideMute && (
        <button
          onClick={() => onSetMuted(!state.muted)}
          style={{
            appearance: "none",
            border: "0.5px solid var(--line)",
            background: state.muted ? "var(--accent-soft)" : "transparent",
            color: state.muted ? "var(--accent)" : "var(--ink-2)",
            borderRadius: 999,
            padding: "6px 12px",
            fontSize: "var(--type-floor)",
            fontWeight: 600,
            cursor: "pointer",
            alignSelf: "flex-start",
            minHeight: 28,
          }}
        >
          {state.muted ? "Muted — tap to unmute" : "Mute"}
        </button>
      )}
    </div>
  );
}
